import { ChevronDown } from '@carbon/react/icons';
import type {
  NodeUnit,
  SlotValuationInputsResponse,
} from 'types/packages-api/slot-valuation-inputs';
import {
  EMPTY,
  formatNodeValue,
  monthLabel,
  seriesExtent,
  type SeriesExtent,
} from './utils';
import { columnExtentNote, seriesColumnHeader } from './source-blocks';
import styles from './slot-tearsheet.module.scss';

/**
 * Caption for a disclosure header: how much of the series is shown, how long it runs, and
 * how much of it is absent.
 *
 * THE VISIBLE ROWS CANNOT SAY THIS. They show 24 months starting at the first; only this
 * line carries the total count and the last month, so a reader who never expands still knows
 * a 361-month series exists and where it ends.
 *
 * @param {number} shown - Rows actually rendered.
 * @param {number} total - Rows in the series.
 * @param {SeriesExtent} extent - Last date and absent-month count.
 * @return {string} The caption.
 */
export function seriesDisclosureCaption(
  shown: number,
  total: number,
  extent: SeriesExtent
): string {
  const parts =
    shown >= total
      ? [`${total} months`]
      : [`first ${shown} of ${total} months`];
  const last = extent.lastDate ? monthLabel(extent.lastDate) : null;
  if (last) {
    parts.push(`through ${last}`);
  }
  // Stated, not interpolated. The rows themselves show the gap by jumping — this says how
  // big it is without inventing a row for any of it.
  if (extent.monthsAbsent > 0) {
    parts.push(
      `${extent.monthsAbsent} month${
        extent.monthsAbsent === 1 ? '' : 's'
      } absent from the series`
    );
  }
  return parts.join(' · ');
}

/**
 * One block's monthly series: MONTHS DOWN, STREAMS ACROSS.
 *
 * ── WHY THIS ORIENTATION ─────────────────────────────────────────────────────────────────
 *
 * It is how every production and cashflow report in this industry is already read, and it
 * makes one thing free that a caption previously had to explain: THE GAP. Three of five
 * measured slots have exactly one break in their monthly series — EC 61's DUC slot jumps
 * from Apr 2025 straight to Apr 2026. With months down the rows, the reader sees the date
 * column jump. Nothing is interpolated and no caption is needed for it.
 *
 * ── ROWS ARE THE MONTHS THAT EXIST ───────────────────────────────────────────────────────
 *
 * A row is never synthesised for a missing month, not even an empty one. A blank row would
 * assert "this month exists and its values are unknown", which is a different and false
 * claim about a month the calculator did not produce. The date column tells the truth by
 * construction.
 *
 * ── AND THERE IS NO TOTALS ROW ───────────────────────────────────────────────────────────
 *
 * No sum, no average, no subtotal, no cumulative column. Every number here is one the
 * calculator wrote; a total would be a number this page invented, and a total over a
 * TRUNCATED, GAPPED window would be wrong even as arithmetic.
 *
 * @param {object} props - The block's columns and the payload.
 * @return {JSX.Element | null} The disclosure, or null when this block has no series.
 */
export function SeriesDisclosure({
  columns,
  response,
  unitFor,
  labelFor,
  lengthFor,
  formulaFor,
}: {
  columns: string[];
  response: SlotValuationInputsResponse;
  unitFor: (key: string) => NodeUnit | undefined;
  labelFor: (key: string) => string;
  /** The column's own non-null count. Undefined means the node declared none. */
  lengthFor: (key: string) => number | undefined;
  /**
   * The column node's own formula, or null.
   *
   * ── THIS IS THE HALF THAT HAD TO MOVE BEFORE THE ROWS COULD GO ─────────────────────────
   *
   * Dropping a disclosed node's parameter row (parameterRowNodes) deletes everything that
   * row carried. Measured across all 45 valued slots readable locally, exactly one thing was
   * not already in the column header: THREE nodes carry a formula — oil_realized_price,
   * gas_realized_price and ngl_realized_price, on 45/45 slots — and the row was the ONLY
   * place on the page they appeared. So they render here instead, and the row deletion is a
   * relocation rather than a trade of information for tidiness.
   */
  formulaFor: (key: string) => string | null;
}) {
  const series = response.outputs?.cashflowSeries;
  if (!columns.length || !series?.rows?.length) {
    return null;
  }
  // ── EVERY MONTH, SCROLLED (UAT 10, 9 Sep) ──────────────────────────────────────────
  //
  // This was `.slice(0, SERIES_ROW_LIMIT)` — 24 of 361. The 24 was purely a RENDER limit:
  // the payload has always carried every row (slot_valuation_inputs.rb:796-801 passes the
  // series through with no cap), so nothing new is fetched to show them.
  //
  // Andrew rejected pagination explicitly, so the rows are all present and the container
  // scrolls. Vertical scrolling is per-disclosure — one expanded series scrolls inside
  // itself rather than the page growing by 361 rows for every block a reader opens.
  const rows = series.rows;
  const extent = seriesExtent(series.rows);

  return (
    <details className={styles.seriesDisclosure}>
      <summary className={styles.seriesSummary}>
        {/* IT HAS TO READ AS EXPANDABLE BEFORE IT IS CLICKED. A bare underlined heading reads
            as a link to somewhere else; the chevron says the content is here and folded. It
            is Carbon's own icon rotated by CSS off `details[open]`, so there is one element
            in one state rather than two icons swapped on a boolean — nothing can render the
            down arrow while the panel is open. `aria-hidden` because <summary> already
            carries the expanded state for a screen reader. */}
        <ChevronDown
          size={16}
          className={styles.seriesChevron}
          aria-hidden="true"
        />
        <span className={styles.seriesSummaryTitle}>
          Monthly series — {columns.length}{' '}
          {columns.length === 1 ? 'column' : 'columns'}
        </span>
        <span className={styles.seriesSummaryCaption}>
          {seriesDisclosureCaption(rows.length, series.rows.length, extent)}
        </span>
      </summary>
      <div className={styles.seriesScroll}>
        <table className={styles.seriesTable}>
          <thead>
            <tr>
              <th scope="col" className={styles.seriesMonthHead}>
                Month
              </th>
              {columns.map((column) => {
                // PER COLUMN, because the caption above cannot speak for all of them:
                // seriesLength is a per-node non-null count and the caption is per block.
                // Rendered only where this column disagrees with the table.
                const note = columnExtentNote(
                  lengthFor(column),
                  series.rows.length
                );
                const formula = formulaFor(column);
                return (
                  <th scope="col" key={column} className={styles.seriesHead}>
                    {seriesColumnHeader(labelFor(column), unitFor(column))}
                    {note && (
                      <span className={styles.seriesHeadNote}>{note}</span>
                    )}
                    {/* RELOCATED FROM THE PARAMETER ROW, not newly invented. Only three of
                        the 34 monthly nodes carry one, so this is quiet on 31 columns and
                        is the sole rendering of those three formulas on the page. */}
                    {formula && (
                      <span className={styles.seriesHeadFormula}>
                        {formula}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date}>
                <th scope="row" className={styles.seriesMonthCell}>
                  {monthLabel(row.date) ?? row.date}
                </th>
                {columns.map((column) => (
                  <td key={column} className={styles.seriesCell}>
                    {row[column] === null || row[column] === undefined
                      ? EMPTY
                      : formatNodeValue(row[column], unitFor(column))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
